import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { MapLayerCreateRequestDto } from "@iventis/domain-model/model/mapLayerCreateRequestDto";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { mappingApi } from "@iventis/api/src/api";
import { LayersCopyApiRequest } from "@iventis/domain-model/model/layersCopyApiRequest";
import { LayersImportApiRequest } from "@iventis/domain-model/model/layersImportApiRequest";
import { AxiosResponse } from "axios";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";
import { getNextSidebarOrderValue } from "../sidebar-order-helpers";
import { MapLayerPatchType } from "../state/map.slice.thunks";

export const postLayer = async (layer: MapLayer, existingLayers: DomainLayer[], options = { keepOrder: false }) => {
    const newSidebarOrder = options.keepOrder ? layer.sidebarOrder : getNextSidebarOrderValue(existingLayers);
    const mapLayerCreateRequest: MapLayerCreateRequestDto = {
        id: layer.id,
        sidebarOrder: newSidebarOrder,
        mapOrder: layer.mapOrder,
        name: layer.name,
        styleType: layer.styleType,
        areaStyle: layer.areaStyle,
        lineStyle: layer.lineStyle,
        pointStyle: layer.pointStyle,
        iconStyle: layer.iconStyle,
        modelStyle: layer.modelStyle,
        lineModelStyle: layer.lineModelStyle,
        areaModelStyle: layer.areaModelStyle,
        tooltipDataFieldIds: layer.tooltipDataFieldIds,
        groupId: layer.groupId,
        templateId: layer.templateId,
    };
    const response = await mappingApi.post<MapLayer, AxiosResponse<MapLayer>, MapLayerCreateRequestDto>(`/maps/${layer.mapId}/layers`, mapLayerCreateRequest, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.layer) }),
            message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.layer) }),
        },
    });
    return response.data;
};

/** Imports the given layers into the given map */
export const importLayers = (mapId: string, imports: LayersImportApiRequest) =>
    mappingApi.post(`/maps/${mapId}/layers/import`, imports, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.import_error.title, { entity: translate(Content.map9.layers) }),
            message: translate(Content.errors.custom_network_error.import_error.message, { entity: translate(Content.map9.layers) }),
        },
    });

/** Copy or Unlink the given layers */
export const copyLayers = (mapId: string, request: LayersCopyApiRequest) =>
    mappingApi.post<unknown, unknown, LayersCopyApiRequest>(`/maps/${mapId}/layers/copy`, request, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.layers) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.layers) }),
        },
    });

export const patchLayers = (mapId: string, mapLayers: MapLayerPatchType[]) => {
    // ensure we send a single polymorphic `style` property when a specific style is present
    type ExtendedPatch = Partial<MapLayerPatchType> & { style?: unknown };

    const payload = mapLayers.map((m) => {
        const copy: ExtendedPatch = { ...m };

        // Select style and discriminator based on the declared styleType
        let selectedStyle: unknown | null = null;
        let discriminator: string | undefined;
        switch (m.styleType) {
            case "Area":
                selectedStyle = copy.areaStyle ?? null;
                discriminator = "AreaStyle";
                break;
            case "Line":
                selectedStyle = copy.lineStyle ?? null;
                discriminator = "LineStyle";
                break;
            case "Point":
                selectedStyle = copy.pointStyle ?? null;
                discriminator = "PointStyle";
                break;
            case "Icon":
                selectedStyle = copy.iconStyle ?? null;
                discriminator = "IconStyle";
                break;
            case "Model":
                selectedStyle = copy.modelStyle ?? null;
                discriminator = "ModelStyle";
                break;
            case "LineModel":
                selectedStyle = copy.lineModelStyle ?? null;
                discriminator = "LineModelStyle";
                break;
            case "AreaModel":
                selectedStyle = copy.areaModelStyle ?? null;
                discriminator = "AreaModelStyle";
                break;
            default:
                selectedStyle = null;
                discriminator = undefined;
        }

        if (selectedStyle != null) {
            // Put discriminator first
            const styleProps = selectedStyle as Record<string, unknown>;
            const styleWithType = { $type: discriminator, ...styleProps };
            copy.style = styleWithType;
            // remove concrete style props to avoid duplication
            delete copy.areaStyle;
            delete copy.lineStyle;
            delete copy.pointStyle;
            delete copy.iconStyle;
            delete copy.modelStyle;
            delete copy.lineModelStyle;
            delete copy.areaModelStyle;
        }

        return copy as MapLayerPatchType;
    });

    return mappingApi.patch(`/maps/${mapId}/layers`, payload, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.layers) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.layers) }),
        },
    });
};

export const deleteLayer = (mapId: string, layerId: string) =>
    mappingApi.delete(`/maps/${mapId}/layers/${layerId}`, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.layers) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.layers) }),
        },
    });
